import { OneClickPropertyDetails } from "@/pages/oneClickPage/oneClickPageSlice";
import { ObjectTypeEnum, PropertyInformation } from "@/types/refineRealEstate";
import {
  REAL_ESTATE_VALIDATION_LIMITS,
  getGarageLimits,
  getLivingAreaLimits,
  getUnitsNumberLimits,
} from "@/validation/realEstateValidationLimits";

const validationLimits = REAL_ESTATE_VALIDATION_LIMITS;

export const isValidGermanPostCode = (postCode?: string | null): boolean =>
  typeof postCode === "string" && postCode.length === 5 && /^[0-9]{5}$/.test(postCode);

export const isValidEnergyCertificateType = (energyClass?: string | null, certificateType?: string | null): boolean =>
  !energyClass || Boolean(certificateType);

export const isValidConstructionYear = (year?: number | null): boolean =>
  year !== undefined &&
  year !== null &&
  Number.isInteger(year) &&
  year >= validationLimits.constructionYear.min &&
  year <= validationLimits.constructionYear.max;

export const isValidLivingArea = (
  area?: number | null,
  objectType?: ObjectTypeEnum | null,
  unitsNumber?: number | null,
): boolean => {
  if (area === undefined || area === null) return false;

  const { min, max } = getLivingAreaLimits(objectType);
  return (
    area >= min &&
    area <= max &&
    ((objectType !== ObjectTypeEnum.multiFamilyHouse && objectType !== ObjectTypeEnum.mixedUseProperty) ||
      unitsNumber == null ||
      area >= unitsNumber)
  );
};

export const isValidUnitsNumber = (unitsNumber?: number | null, objectType?: ObjectTypeEnum | null): boolean => {
  if (objectType !== ObjectTypeEnum.multiFamilyHouse && objectType !== ObjectTypeEnum.mixedUseProperty) return true;

  const limits = getUnitsNumberLimits(objectType);
  return (
    limits !== undefined &&
    unitsNumber !== undefined &&
    unitsNumber !== null &&
    Number.isInteger(unitsNumber) &&
    unitsNumber >= limits.min &&
    (limits.max === undefined || unitsNumber <= limits.max)
  );
};

export const isValidRooms = (rooms?: number | null | undefined): boolean =>
  rooms ? rooms >= validationLimits.rooms.min && rooms <= validationLimits.rooms.max : true;

export const isValidGarages = (garages?: number | null | undefined): boolean => {
  if (garages === undefined || garages === null) return true;

  const limits = getGarageLimits();
  return Number.isInteger(garages) && garages >= limits.min && garages <= limits.max;
};

export const isValidOutdoorParkingSpaces = (spaces?: number | null | undefined): boolean => {
  if (spaces === undefined || spaces === null) return true;

  return (
    Number.isInteger(spaces) &&
    spaces >= validationLimits.outdoorParkingSpaces.min &&
    spaces <= validationLimits.outdoorParkingSpaces.max
  );
};

export const isValidNumberOfFloors = (floors?: number | null): boolean =>
  floors !== undefined &&
  floors !== null &&
  Number.isInteger(floors) &&
  floors >= validationLimits.numberOfFloors.min &&
  floors <= validationLimits.numberOfFloors.max;

export const isValidCommercialArea = (area?: number | null): boolean =>
  area !== undefined && area !== null && Number.isFinite(area) && area > 0;

export const isValidCommercialUnits = (units?: number | null): boolean =>
  units !== undefined && units !== null && Number.isInteger(units) && units >= 1 && units <= 998;

export const isValidPlotArea = (area?: number | null): boolean =>
  area !== undefined && area !== null && area >= validationLimits.plotArea.min && area <= validationLimits.plotArea.max;

export const isValidPVConstructionYear = (
  available: boolean | null | undefined,
  PVyear?: number | null,
  constructionYear?: number | null,
): boolean => {
  if (!available || available === null) return true;
  if (available === true && PVyear != null) {
    const minYear =
      constructionYear !== undefined &&
      constructionYear !== null &&
      constructionYear >= validationLimits.photovoltaicConstructionYear.min
        ? constructionYear
        : validationLimits.photovoltaicConstructionYear.min;
    return (
      Number.isInteger(PVyear) &&
      PVyear >= minYear &&
      PVyear <= validationLimits.photovoltaicConstructionYear.preValidationMax
    );
  }
  return false;
};

export const isValidPVYield = (available: boolean | null | undefined, yieldPV?: number | null): boolean => {
  if (!available || available === null) return true;
  return available === true && yieldPV != null
    ? Number.isInteger(yieldPV) &&
        yieldPV >= validationLimits.photovoltaicAnnualPowerYield.min &&
        yieldPV <= validationLimits.photovoltaicAnnualPowerYield.max
    : false;
};

export const isValidRefurbishmentYear = (
  refurbishmentYear?: number | null,
  constructionYear?: number | null,
): boolean => {
  if (refurbishmentYear === undefined || refurbishmentYear === null) return true;

  if (
    constructionYear !== undefined &&
    constructionYear !== null &&
    Number.isInteger(constructionYear) &&
    constructionYear >= validationLimits.constructionYear.min &&
    constructionYear <= validationLimits.constructionYear.max
  ) {
    return (
      Number.isInteger(refurbishmentYear) &&
      refurbishmentYear >= constructionYear &&
      refurbishmentYear <= validationLimits.refurbishmentYear.max
    );
  }

  return (
    Number.isInteger(refurbishmentYear) &&
    refurbishmentYear >= validationLimits.refurbishmentYear.min &&
    refurbishmentYear <= validationLimits.refurbishmentYear.max
  );
};

export const isDataForSoneValid = (
  propertyInformationState: PropertyInformation | OneClickPropertyDetails,
): boolean => {
  if (
    !isValidConstructionYear(propertyInformationState.buildingData.constructionYear) ||
    (propertyInformationState.generalInfo.objectType === ObjectTypeEnum.mixedUseProperty &&
      (!isValidCommercialArea(propertyInformationState.buildingData.commercialArea) ||
        !isValidCommercialUnits(propertyInformationState.buildingData.commercialUnits))) ||
    !isValidLivingArea(
      propertyInformationState.buildingData.livingArea,
      propertyInformationState.generalInfo.objectType,
      propertyInformationState.buildingData.unitsNumber,
    ) ||
    (propertyInformationState.generalInfo.objectType === ObjectTypeEnum.house &&
      !isValidPlotArea(propertyInformationState.plotData.plotArea)) ||
    !isValidGarages(propertyInformationState.buildingData.garages) ||
    !isValidOutdoorParkingSpaces(propertyInformationState.buildingData.outdoorParkingSpaces) ||
    !isValidEnergyCertificateType(
      propertyInformationState.energyAndPhotovoltaic.energyCertificate,
      propertyInformationState.energyAndPhotovoltaic.certificateType,
    ) ||
    !isValidPVConstructionYear(
      propertyInformationState.energyAndPhotovoltaic.photovoltaicAvailable,
      propertyInformationState.energyAndPhotovoltaic.constructionYear,
    ) ||
    !isValidPVYield(
      propertyInformationState.energyAndPhotovoltaic.photovoltaicAvailable,
      propertyInformationState.energyAndPhotovoltaic.annualPowerYield,
    ) ||
    !isValidRefurbishmentYear(
      propertyInformationState.modernization.refurbishmentYear,
      propertyInformationState.buildingData.constructionYear,
    )
  )
    return false;
  return true;
};

export const isDataForAvmValid = (propertyInformationState: PropertyInformation): boolean => {
  if (
    !isValidConstructionYear(propertyInformationState.buildingData.constructionYear) ||
    (propertyInformationState.generalInfo.objectType === ObjectTypeEnum.mixedUseProperty &&
      (!isValidCommercialArea(propertyInformationState.buildingData.commercialArea) ||
        !isValidCommercialUnits(propertyInformationState.buildingData.commercialUnits))) ||
    !isValidLivingArea(
      propertyInformationState.buildingData.livingArea,
      propertyInformationState.generalInfo.objectType,
      propertyInformationState.buildingData.unitsNumber,
    ) ||
    (propertyInformationState.generalInfo.objectType === ObjectTypeEnum.house &&
      !isValidPlotArea(propertyInformationState.plotData.plotArea)) ||
    !isValidRooms(propertyInformationState.buildingData.rooms) ||
    !isValidEnergyCertificateType(
      propertyInformationState.energyAndPhotovoltaic.energyCertificate,
      propertyInformationState.energyAndPhotovoltaic.certificateType,
    ) ||
    !isValidPVConstructionYear(
      propertyInformationState.energyAndPhotovoltaic.photovoltaicAvailable,
      propertyInformationState.energyAndPhotovoltaic.constructionYear,
    ) ||
    !isValidPVYield(
      propertyInformationState.energyAndPhotovoltaic.photovoltaicAvailable,
      propertyInformationState.energyAndPhotovoltaic.annualPowerYield,
    )
  )
    return false;
  return true;
};
